import {
  AppBar,
  HHBreadcrumbs,
  HHLink,
  HHTypography,
} from '@hinge-health/react-component-library';
import { matchPath, useLocation } from 'react-router-dom';
import { routes } from '../../constants/strings/routes';
import { BillingSubMenu } from '../billing-sub-menu';

export const BILLING_TOOL_APP_BAR = 'billing-tool-app-tool-bar';

// we need to use the full path for the breadcrumb to match against the location pathname
const breadcrumbPathPatterns = [
  {
    path: routes.billing.home,
    text: 'Home',
    params: [],
  },
  {
    path: `${routes.billing.home}/${routes.billing.userPath}/${routes.billing.userId}/*`,
    text: 'User Billing Journey',
    params: [':id'],
  },
  {
    path: `${routes.billing.home}${routes.billing.billManagement}`,
    text: 'Bill Management',
    params: [],
  },
  {
    path: `${routes.billing.home}${routes.billing.submissionsManagement}`,
    text: 'Submission Management',
    params: [],
  },
  {
    path: `${routes.billing.home}${routes.billing.unbilledReport}`,
    text: 'Unbilled Report',
    params: [],
  },
  {
    path: `${routes.billing.home}${routes.billing.holdsAggregate}`,
    text: 'Aggregate Holds',
    params: [],
  },
  {
    path: `${routes.billing.home}/${routes.billing.discountCalculator}`,
    text: 'Discount Calculator',
    params: [],
  },
  {
    path: `${routes.billing.home}${routes.billing.batchContractCreate}`,
    text: 'Batch Create Contract',
    params: [],
  },
  {
    path: `${routes.billing.main}/${routes.billing.batchContractCreateStatus}`,
    text: 'Batch Create Contract Status',
    params: [],
  },
  {
    path: `${routes.billing.home}${routes.billing.fullNewContractPackagePath}`,
    text: 'New Contract',
    params: [':id'],
  },
  {
    path: `${routes.billing.home}${routes.billing.fullEditContractPackagePath}`,
    text: 'Edit Contract',
    params: [':id', ':contractId'],
  },
  {
    path: routes.billing.tool,
    text: 'Tool',
    params: [],
  },
  {
    path: routes.billing.main,
    text: 'Client Insurers',
    params: [],
  },
  {
    path: `${routes.billing.main}/:id`,
    text: 'Client Insurer',
    params: [':id'],
  },
  {
    path: `${routes.billing.home}/${routes.billing.clientPath}/:id`,
    text: 'Client',
    params: [':id'],
    end: false,
  },
  {
    path: `${routes.billing.home}${routes.billing.batchProgramCapImport}`,
    text: 'Program Cap Batch Import',
    params: [],
    parent: {
      text: 'Program Caps',
      href: `${routes.billing.home}${routes.billing.programCaps}`,
    },
  },
  {
    path: `${routes.billing.home}${routes.billing.programCaps}`,
    text: 'Program Caps',
    params: [],
  },
  {
    path: `${routes.billing.home}${routes.billing.clientFeeSchedules}`,
    text: 'Client Fee Schedules',
    params: [],
  },
  {
    path: `${routes.billing.home}${routes.billing.partnershipFeeSchedules}`,
    text: 'Partnership Fee Schedules',
    params: [],
  },
  {
    path: `${routes.billing.home}${routes.billing.esiFeeSchedules}`,
    text: 'ESI UPC Schedules',
    params: [],
  },
  {
    path: `${routes.billing.home}${routes.billing.cvsFeeSchedules}`,
    text: 'CVS UPC Schedules',
    params: [],
  },
  {
    path: `${routes.billing.home}${routes.billing.procedureCodes}`,
    text: 'CPT Codes',
    params: [],
  },
  {
    path: `${routes.billing.home}${routes.billing.icdCodes}`,
    text: 'ICD Codes',
    params: [],
  },
  {
    path: `${routes.billing.home}${routes.billing.revenueShares}`,
    text: 'Revenue Shares',
    params: [],
  },
  {
    path: `${routes.billing.home}${routes.billing.volumeBasedDiscounts}`,
    text: 'Volume Based Discounts',
    params: [],
  },
  {
    path: `${routes.billing.home}${routes.billing.packages}`,
    text: 'Packages',
    params: [],
  },
  {
    path: `${routes.billing.home}${routes.billing.eligibilityVerifier}`,
    text: 'Eligibility Verifier',
    params: [],
  },
  {
    path: `${routes.billing.home}${routes.billing.csvUpload}`,
    text: 'CSV Upload',
    params: [],
  },
];

const BreadCrumb = (): JSX.Element | null => {
  const location = useLocation();

  const match = breadcrumbPathPatterns.find(p =>
    matchPath(p.path, location.pathname),
  );

  if (!match) return null;

  const { text, parent } = match;
  return (
    <HHBreadcrumbs aria-label="breadcrumb">
      <HHLink color="inherit" href={routes.billing.home} underline="hover">
        Operations Hub
      </HHLink>
      <HHLink color="inherit" href={routes.billing.home} underline="hover">
        Billing
      </HHLink>
      {parent && (
        <HHLink color="inherit" href={parent.href} underline="hover">
          {parent.text}
        </HHLink>
      )}
      <HHTypography color="text.primary" hhVariant="variant-bypass">
        {text}
      </HHTypography>
    </HHBreadcrumbs>
  );
};

const BillingToolHeader = (): JSX.Element => (
  <AppBar
    elevation={3}
    variant="elevation"
    color="inherit"
    position="sticky"
    sx={{
      flexDirection: 'row',
      alignItems: 'center',
      padding: 2,
      gap: 2,
    }}
    data-testid={BILLING_TOOL_APP_BAR}
  >
    <BillingSubMenu />
    <BreadCrumb />
  </AppBar>
);

export default BillingToolHeader;
